import { useMemo } from 'react';
import { IconAnalytics } from '@components/icons/icon-analytics';
import { IconHome } from '@components/icons/icon-home';
import { MenuButtonActions } from '@components/menu-button-actions';
import {
  ENABLED_BOX_OFFICE_BUTTON,
  ENABLED_CREATE_EVENT_BUTTON,
  ENABLED_INVITATIONS_BUTTON,
  ENABLED_OFFLINE_SALES_BUTTON,
} from '@constants/company-auth-permissions';
import { useEventCreationRevoked } from '@hooks/use-event-creation-revoked/use-event-creation-revoked';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';
import { getFilteredModalActions } from '@utils/get-filtered-modal-actions';
import classNames from 'classnames';

import type { MobileMenuProps } from './mobile-menu.type';

export const MobileMenu = ({ location, navigateTo }: MobileMenuProps) => {
  const isEventCreationEnabled = useIsProtectedElement({ permissions: ENABLED_CREATE_EVENT_BUTTON });
  const { isEventCreationRevoked, revokedReason } = useEventCreationRevoked();
  const isInvitationsEnabled = useIsProtectedElement({ permissions: ENABLED_INVITATIONS_BUTTON });
  const isOfflineSalesEnabled = useIsProtectedElement({ permissions: ENABLED_OFFLINE_SALES_BUTTON });
  const isBoxOfficeEnabled = useIsProtectedElement({ permissions: ENABLED_BOX_OFFICE_BUTTON });

  const filteredActions = useMemo(
    () =>
      getFilteredModalActions({
        offlineSale: isOfflineSalesEnabled,
        inviteGuest: isInvitationsEnabled,
        boxOffice: isBoxOfficeEnabled,
      }),
    [isOfflineSalesEnabled, isInvitationsEnabled, isBoxOfficeEnabled]
  );

  return (
    <nav className='fixed bottom-0 w-full py-2 pb-safe bg-primary shadow-nav-menu flex items-center justify-around z-40'>
      <button className='flex-col justify-center items-center grow shrink-0 py-3'>
        <IconHome
          onClick={() => navigateTo('events/upcoming')}
          className={classNames('w-6 h-6 mx-auto', {
            'text-primary': location.pathname.includes('/events'),
            'text-soft': !location.pathname.includes('/events'),
          })}></IconHome>
      </button>
      <div className='w-20 flex items-center justify-center'>
        <MenuButtonActions
          actions={filteredActions}
          navigateToCreateEvent={isEventCreationEnabled ? () => navigateTo('events/info') : undefined}
          createEventDisabledReason={isEventCreationRevoked ? revokedReason : undefined}
          customClassNames='grow shrink-0 w-20'
          actionButtonCustomClasses='p-2 rounded-xl w-10 h-10 mx-auto shrink-0  '
        />
      </div>
      <button className='flex-col justify-center items-center grow shrink-0 py-3'>
        <IconAnalytics
          onClick={() => navigateTo('analytics')}
          className={classNames('w-6 h-6 mx-auto', {
            'text-primary': location.pathname.includes(`/analytics`),
            'text-soft': !location.pathname.includes('/analytics'),
          })}></IconAnalytics>
      </button>
    </nav>
  );
};
